/**
 * Trustpilot Reviews — data-driven renderer + carousel + AI summary modal.
 *
 * Data sources:
 *   - window.NextStepTPData  (assets/trustpilot-reviews-data.js): every 4–5★
 *     review (original text + es/en translations) sorted by date desc, plus
 *     the AI summary in es/en.
 *   - window.NextStepTPI18n  (assets/trustpilot-reviews-i18n-<lang>.js):
 *     optional per-locale translation pack { summary, reviews: {id:[t,x]} },
 *     loaded conditionally by Liquid for non-es/en locales.
 *
 * Renders into any container with [data-tp-reviews]:
 *
 *   <div class="customer-reviews__inner" data-tp-reviews
 *        data-autoplay="5000|false" data-url="https://es.trustpilot.com/review/…"
 *        data-show-author="true|false" data-show-date="true|false"
 *        data-i18n='{"verified":"…","translated":"…","aiClose":"…","aiCta":"…"}'>
 *     <div class="customer-reviews__track"></div>
 *     …arrow buttons…
 *   </div>
 *
 * Language: original text is shown when the review was written in the
 * visitor's language; otherwise the locale pack translation, falling back to
 * es (es/ca visitors) or en, with a "Translated" badge.
 */
(function () {
  'use strict';

  // DOM window: only INITIAL_CARDS are rendered up front; the carousel asks
  // for CHUNK more whenever it gets within LOOKAHEAD cards of the rendered
  // end (arrows, autoplay, swipe or native scroll). With 200+ reviews this
  // keeps the page at a few dozen cards instead of rendering all of them.
  var INITIAL_CARDS = 12;
  var CHUNK = 12;
  var LOOKAHEAD = 6;
  // Carousels only carry the newest reviews (theme setting
  // "Reviews in the carousel", data-max-reviews). The rest stay out of the
  // DOM until the shopper opens the full list inside the AI summary modal.
  var DEFAULT_MAX_REVIEWS = 25;
  var LIST_CHUNK = 20;

  function getLang() {
    var iso = (document.documentElement.lang || 'es').toLowerCase();
    return iso.split('-')[0];
  }

  function getPack(lang) {
    var packs = window.NextStepTPI18n || {};
    return packs[lang] || null;
  }

  function pickContent(review, lang, pack) {
    if (review.l === lang) return { t: review.t, x: review.x, translated: false };
    if (pack && pack.reviews && pack.reviews[review.id]) {
      var p = pack.reviews[review.id];
      return { t: p[0], x: p[1], translated: true };
    }
    if (lang === 'es' && review.es) return { t: review.es[0], x: review.es[1], translated: true };
    if (lang === 'en' && review.en) return { t: review.en[0], x: review.en[1], translated: true };
    if (review.en) return { t: review.en[0], x: review.en[1], translated: true };
    if (review.es) return { t: review.es[0], x: review.es[1], translated: true };
    return { t: review.t, x: review.x, translated: false };
  }

  function pickSummary(data, lang, pack) {
    if (pack && pack.summary) return pack.summary;
    if ((lang === 'es' || lang === 'ca') && data.summary.es) return data.summary.es;
    return data.summary.en || data.summary.es;
  }

  var dateFormatter = null;
  function formatDate(iso) {
    if (!dateFormatter) {
      try {
        dateFormatter = new Intl.DateTimeFormat(document.documentElement.lang || 'es', {
          year: 'numeric', month: 'short', day: 'numeric'
        });
      } catch (e) {
        dateFormatter = { format: function (d) { return d.toISOString().slice(0, 10); } };
      }
    }
    var parts = iso.split('-');
    return dateFormatter.format(new Date(+parts[0], +parts[1] - 1, +parts[2]));
  }

  var VERIFIED_SVG = '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14ZM6.09217 7.81401L9.20311 4.7031C9.44874 4.45757 9.84688 4.45757 10.0923 4.7031C10.338 4.94864 10.338 5.34673 10.0923 5.59226L6.62009 9.06448C6.59573 9.10283 6.56682 9.13912 6.53333 9.17256C6.28787 9.41821 5.88965 9.41821 5.64402 9.17256L3.7059 7.11031C3.46046 6.86464 3.46046 6.46669 3.7059 6.22102C3.95154 5.97548 4.34968 5.97548 4.59512 6.22102L6.09217 7.81401Z" fill="currentColor"/></svg>';
  var SPARKLE_SVG = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 1.5l1.9 4.9a1 1 0 00.57.57L17.4 8.9a.6.6 0 010 1.12l-4.93 1.93a1 1 0 00-.57.57L10 17.4a.6.6 0 01-1.12 0l-1.93-4.88a1 1 0 00-.57-.57L1.45 10a.6.6 0 010-1.12l4.93-1.93a1 1 0 00.57-.57L8.88 1.5a.6.6 0 011.12 0z" fill="currentColor"/></svg>';
  var TP_STAR_SVG = '<svg viewBox="0 0 56 50" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M30.141 0l-6.062 18.633H4.603L19.528 30.14l-5.7 18.634L30.14 36.26l16.312 12.515-6.063-18.634L55.68 18.633H36.204z" fill="#00b67a"/><path d="M42.088 34.155l-2.445-7.543L30.14 36.26z" fill="#005128"/></svg>';

  /* Minimal outline icons for the 4 summary topics (product / shipping / support / trust) */
  var TOPIC_ICONS = [
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M4 7.5l8 4.5 8-4.5M12 12v9" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.5 6h11v11h-11zM13.5 10h4l3 3v4h-7" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="6.5" cy="17.5" r="1.8" stroke="currentColor" stroke-width="1.6"/><circle cx="17" cy="17.5" r="1.8" stroke="currentColor" stroke-width="1.6"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 01-9 8.4 9 9 0 01-3.9-.9L3 20l1-4.1a8.4 8.4 0 1117-4.4z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M8 10.5h8M8 13.5h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2.5l7.5 3v6c0 4.7-3.2 8.3-7.5 10-4.3-1.7-7.5-5.3-7.5-10v-6l7.5-3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M8.5 11.8l2.4 2.4 4.6-4.7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  ];

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* "Emanuele Vitucci" → "Emanuele V."; single-word names stay unchanged. */
  function obfuscateName(name) {
    var parts = String(name || '').trim().split(/\s+/);
    if (parts.length < 2) return name;
    return parts[0] + ' ' + parts[1].charAt(0).toUpperCase() + '.';
  }

  /* Cut a review text at `max` characters (theme setting), stepping back to
     the previous word boundary so no word is split, and appending "…". */
  function truncateText(text, max) {
    var s = String(text == null ? '' : text);
    if (!max || s.length <= max) return { text: s, truncated: false };
    var cut = s.slice(0, max);
    var sp = cut.lastIndexOf(' ');
    if (sp > max * 0.6) cut = cut.slice(0, sp);
    return { text: cut.replace(/[\s.,;:]+$/, '') + '…', truncated: true };
  }

  // The white star itself is a CSS background on .customer-reviews__star
  // (trustpilot-reviews.css), so each star is one element, not three.
  function starsHtml(n) {
    var html = '<div class="customer-reviews__stars">';
    for (var s = 1; s <= 5; s++) {
      html += '<span class="customer-reviews__star' + (s <= n ? ' customer-reviews__star--filled' : '') + '"></span>';
    }
    return html + '</div>';
  }

  function cardHtml(review, lang, opts, idx) {
    var content = pickContent(review, lang, opts.pack);
    var body = truncateText(content.x, opts.truncateChars);
    var html = '<a class="customer-reviews__card" href="' + escapeHtml(opts.url) + '" target="_blank" rel="noopener noreferrer">';
    html += '<div class="customer-reviews__card-inner">';
    html += '<div class="customer-reviews__card-header">' + starsHtml(review.s);
    html += '<span class="customer-reviews__verified"><span class="customer-reviews__verified-icon">' + VERIFIED_SVG + '</span>' + escapeHtml(opts.i18n.verified) + '</span></div>';
    html += '<h4 class="customer-reviews__card-title">' + escapeHtml(content.t) + '</h4>';
    html += '<p class="customer-reviews__card-text">' + escapeHtml(body.text).replace(/\n/g, '<br>')
      + (body.truncated ? ' <button type="button" class="customer-reviews__read-more" data-tp-read-more="' + idx + '">' + escapeHtml(opts.i18n.readMore) + '</button>' : '')
      + '</p>';
    var metaParts = [];
    if (opts.showAuthor) {
      var authorName = opts.obfuscateAuthor ? obfuscateName(review.a) : review.a;
      metaParts.push('<span class="customer-reviews__author-name">' + escapeHtml(authorName) + '</span>');
    }
    if (opts.showDate) metaParts.push('<span class="customer-reviews__author-date">' + escapeHtml(formatDate(review.d)) + '</span>');
    if (content.translated) metaParts.push('<span class="customer-reviews__translated-badge">' + escapeHtml(opts.i18n.translated) + '</span>');
    if (metaParts.length) {
      html += '<div class="customer-reviews__card-author">' + metaParts.join('<span class="customer-reviews__meta-sep">·</span>') + '</div>';
    }
    html += '</div></a>';
    return html;
  }

  /* ------------------------------------------------------------------ */
  /* Carousel                                                            */
  /* ------------------------------------------------------------------ */

  // onIndex(index) is called with every index the carousel is about to show
  // (before clamping) so the renderer can append cards ahead of it.
  function initCarousel(inner, track, onIndex) {
    var prev = inner.querySelector('.customer-reviews__arrow--prev');
    var next = inner.querySelector('.customer-reviews__arrow--next');
    var currentIndex = 0;

    function notify(index) {
      if (onIndex) onIndex(index);
    }

    function totalCards() {
      return track.querySelectorAll('.customer-reviews__card').length;
    }

    function getCardWidth() {
      var card = track.querySelector('.customer-reviews__card');
      if (!card) return 0;
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      return card.offsetWidth + gap;
    }

    function getStep() {
      var card = track.querySelector('.customer-reviews__card');
      if (!card) return 1;
      // Whole cards only: with 2.5 visible, paging by 3 would skip past the
      // half-visible one.
      var step = Math.floor(track.clientWidth / card.offsetWidth);
      return Math.max(1, step);
    }

    function scrollToIndex(index) {
      if (index < 0) index = 0;
      notify(index);
      var maxIndex = totalCards() - 1;
      if (index > maxIndex) index = maxIndex;
      currentIndex = index;
      var w = getCardWidth();
      var maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: Math.min(currentIndex * w, maxScroll), behavior: 'smooth' });
    }

    var autoplayData = inner.getAttribute('data-autoplay');
    var autoplayInterval = (autoplayData && autoplayData !== 'false') ? parseInt(autoplayData, 10) : 0;
    var autoplayTimer = null;
    var isVisible = false;

    function startAutoplay() {
      if (!autoplayInterval || autoplayTimer) return;
      autoplayTimer = setInterval(function () {
        var step = getStep();
        // Extend the rendered window first, so the end check below only
        // wraps to the start once every review has really been shown.
        notify(currentIndex + step);
        if (currentIndex >= totalCards() - step) {
          currentIndex = 0;
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollToIndex(currentIndex + step);
        }
      }, autoplayInterval);
    }

    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }

    function resetAutoplay() {
      if (!autoplayInterval || !isVisible) return;
      stopAutoplay();
      startAutoplay();
    }

    if (prev) prev.addEventListener('click', function () { scrollToIndex(currentIndex - getStep()); resetAutoplay(); });
    if (next) next.addEventListener('click', function () { scrollToIndex(currentIndex + getStep()); resetAutoplay(); });

    var touchStartX = 0;
    track.addEventListener('touchstart', function (e) { touchStartX = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 30) {
        var w = getCardWidth();
        if (w > 0) currentIndex = Math.round(track.scrollLeft / w);
        notify(currentIndex);
        resetAutoplay();
      }
    }, { passive: true });

    // Native scrolling (trackpad, scrollbar, scroll-snap momentum) bypasses the
    // arrows, so watch the scroll position too.
    var scrollTicking = false;
    track.addEventListener('scroll', function () {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(function () {
        scrollTicking = false;
        var w = getCardWidth();
        if (w > 0) notify(Math.round(track.scrollLeft / w));
      });
    }, { passive: true });

    if (autoplayInterval > 0) {
      var observer = new IntersectionObserver(function (entries) {
        isVisible = entries[0].isIntersecting && entries[0].intersectionRatio >= 1;
        if (isVisible) startAutoplay(); else stopAutoplay();
      }, { threshold: 1.0 });
      observer.observe(inner);
    }
  }

  /* ------------------------------------------------------------------ */
  /* AI summary modal                                                    */
  /* ------------------------------------------------------------------ */

  var modalEl = null;
  var lastFocus = null;

  function ratingDisplay(rating, lang) {
    var s = String(rating);
    return (lang === 'en') ? s : s.replace('.', ',');
  }

  // One <article> per review for the full list in the AI summary modal.
  // Full text (no truncation) — the list is the "read everything" surface.
  function listItemHtml(review, lang, opts) {
    var content = pickContent(review, lang, opts.pack);
    var metaParts = [];
    if (opts.showAuthor) {
      var authorName = opts.obfuscateAuthor ? obfuscateName(review.a) : review.a;
      metaParts.push('<span class="customer-reviews__author-name">' + escapeHtml(authorName) + '</span>');
    }
    metaParts.push('<span class="customer-reviews__author-date">' + escapeHtml(formatDate(review.d)) + '</span>');
    if (content.translated) metaParts.push('<span class="customer-reviews__translated-badge">' + escapeHtml(opts.i18n.translated) + '</span>');
    return '<article class="tp-ai-modal__review">'
      + '<div class="tp-ai-modal__review-header">' + starsHtml(review.s)
      + '<span class="customer-reviews__verified"><span class="customer-reviews__verified-icon">' + VERIFIED_SVG + '</span>' + escapeHtml(opts.i18n.verified) + '</span>'
      + '</div>'
      + '<h4 class="tp-ai-modal__review-title">' + escapeHtml(content.t) + '</h4>'
      + '<p class="tp-ai-modal__review-text">' + escapeHtml(content.x).replace(/\n/g, '<br>') + '</p>'
      + '<div class="tp-ai-modal__review-meta">' + metaParts.join('<span class="customer-reviews__meta-sep">·</span>') + '</div>'
      + '</article>';
  }

  // Renders the list in chunks as it is scrolled, so opening it stays cheap
  // even with 200+ reviews.
  function mountReviewList(modal, data, lang, opts) {
    var wrap = modal.querySelector('[data-tp-all-list]');
    var items = modal.querySelector('[data-tp-all-items]');
    var sentinel = modal.querySelector('[data-tp-all-sentinel]');
    var body = modal.querySelector('.tp-ai-modal__body');
    if (!wrap || !items) return;
    var reviews = data.reviews;
    var rendered = 0;
    function renderChunk() {
      if (rendered >= reviews.length) {
        if (sentinel) sentinel.remove();
        return;
      }
      var html = '';
      var end = Math.min(rendered + LIST_CHUNK, reviews.length);
      for (var i = rendered; i < end; i++) html += listItemHtml(reviews[i], lang, opts);
      rendered = end;
      var frag = document.createElement('template');
      frag.innerHTML = html;
      items.appendChild(frag.content);
      if (rendered >= reviews.length && sentinel) sentinel.remove();
    }
    renderChunk();
    if (sentinel && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        renderChunk();
        if (rendered >= reviews.length) observer.disconnect();
      }, { root: body || null, rootMargin: '400px 0px' });
      observer.observe(sentinel);
    } else if (sentinel) {
      // No IntersectionObserver: render the rest in one go.
      while (rendered < reviews.length) renderChunk();
    }
  }

  function buildModal(data, lang, i18n, url, pack, opts) {
    // Rating/total shown in the hero follow the theme's general Trustpilot
    // settings (passed via data-rating / data-total-reviews on the container)
    // so they always match the count displayed next to the AI button; the
    // data.js snapshot values are only a fallback.
    var heroRating = (opts && opts.rating) || data.rating;
    var heroTotal = (opts && opts.totalReviews) || data.total;
    var s = pickSummary(data, lang, pack);
    var el = document.createElement('div');
    el.className = 'tp-ai-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', s.title);
    var topicsHtml = '';
    for (var i = 0; i < s.topics.length; i++) {
      topicsHtml += '<div class="tp-ai-modal__topic">'
        + '<span class="tp-ai-modal__topic-icon">' + (TOPIC_ICONS[i] || TOPIC_ICONS[0]) + '</span>'
        + '<div class="tp-ai-modal__topic-body">'
        + '<span class="tp-ai-modal__topic-name">' + escapeHtml(s.topics[i].k) + '</span>'
        + '<span class="tp-ai-modal__topic-text">' + escapeHtml(s.topics[i].t) + '</span>'
        + '</div></div>';
    }
    el.innerHTML =
      '<div class="tp-ai-modal__backdrop" data-tp-ai-close></div>' +
      '<div class="tp-ai-modal__panel" role="document">' +
        '<button type="button" class="tp-ai-modal__close" data-tp-ai-close aria-label="' + escapeHtml(i18n.aiClose) + '">' +
          '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="tp-ai-modal__body">' +
          '<div class="tp-ai-modal__hero">' +
            '<span class="tp-ai-modal__logo">' + TP_STAR_SVG + '<span class="tp-ai-modal__logo-word">Trustpilot</span></span>' +
            '<div class="tp-ai-modal__score">' + escapeHtml(ratingDisplay(heroRating, lang)) + '</div>' +
            starsHtml(5) +
            '<div class="tp-ai-modal__count">' + escapeHtml(String(heroTotal)) + '</div>' +
          '</div>' +
          '<div class="tp-ai-modal__divider"></div>' +
          '<div class="tp-ai-modal__ai-pill">' + SPARKLE_SVG + '<span>' + escapeHtml(s.note) + '</span></div>' +
          '<h3 class="tp-ai-modal__title">' + escapeHtml(s.title) + '</h3>' +
          '<p class="tp-ai-modal__text">' + escapeHtml(s.text) + '</p>' +
          '<div class="tp-ai-modal__topics">' + topicsHtml + '</div>' +
          '<a class="tp-ai-modal__cta" href="' + escapeHtml(url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(i18n.aiCta) + '</a>' +
          '<div class="tp-ai-modal__all">' +
            '<button type="button" class="tp-ai-modal__all-button" data-tp-all-open>' +
              escapeHtml(String(i18n.allReviews).replace('{count}', String(data.reviews.length))) +
              '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>' +
            '<div class="tp-ai-modal__list" data-tp-all-list hidden>' +
              '<h4 class="tp-ai-modal__list-title">' + escapeHtml(String(i18n.allReviewsTitle).replace('{count}', String(data.reviews.length))) + '</h4>' +
              '<div data-tp-all-items></div>' +
              '<div class="tp-ai-modal__list-sentinel" data-tp-all-sentinel aria-hidden="true"></div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-tp-ai-close]')) { closeModal(); return; }
      var allBtn = e.target.closest('[data-tp-all-open]');
      if (allBtn) {
        var list = el.querySelector('[data-tp-all-list]');
        if (!list) return;
        allBtn.remove();
        list.hidden = false;
        mountReviewList(el, data, lang, opts);
      }
    });
    return el;
  }

  function openModal(data, lang, i18n, url, pack, opts) {
    if (!modalEl) modalEl = buildModal(data, lang, i18n, url, pack, opts);
    lastFocus = document.activeElement;
    modalEl.classList.add('tp-ai-modal--open');
    document.documentElement.classList.add('tp-ai-modal-open');
    var closeBtn = modalEl.querySelector('.tp-ai-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.classList.remove('tp-ai-modal--open');
    document.documentElement.classList.remove('tp-ai-modal-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Full-review modal (opened from a truncated card's "read more")      */
  /* Reuses the tp-ai-modal chrome (backdrop / panel / close button).    */
  /* ------------------------------------------------------------------ */

  var reviewModalEl = null;

  function openReviewModal(review, lang, opts) {
    var content = pickContent(review, lang, opts.pack);
    if (!reviewModalEl) {
      reviewModalEl = document.createElement('div');
      reviewModalEl.className = 'tp-ai-modal tp-review-modal';
      reviewModalEl.setAttribute('role', 'dialog');
      reviewModalEl.setAttribute('aria-modal', 'true');
      reviewModalEl.innerHTML =
        '<div class="tp-ai-modal__backdrop" data-tp-review-close></div>' +
        '<div class="tp-ai-modal__panel" role="document">' +
          '<button type="button" class="tp-ai-modal__close" data-tp-review-close aria-label="' + escapeHtml(opts.i18n.aiClose) + '">' +
            '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
          '</button>' +
          '<div class="tp-ai-modal__body tp-review-modal__body"></div>' +
        '</div>';
      document.body.appendChild(reviewModalEl);
      reviewModalEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-tp-review-close]')) closeReviewModal();
      });
    }
    var metaParts = [];
    if (opts.showAuthor) {
      var authorName = opts.obfuscateAuthor ? obfuscateName(review.a) : review.a;
      metaParts.push('<span class="customer-reviews__author-name">' + escapeHtml(authorName) + '</span>');
    }
    if (opts.showDate) metaParts.push('<span class="customer-reviews__author-date">' + escapeHtml(formatDate(review.d)) + '</span>');
    if (content.translated) metaParts.push('<span class="customer-reviews__translated-badge">' + escapeHtml(opts.i18n.translated) + '</span>');
    reviewModalEl.querySelector('.tp-review-modal__body').innerHTML =
      starsHtml(review.s) +
      '<h3 class="tp-review-modal__title">' + escapeHtml(content.t) + '</h3>' +
      '<p class="tp-review-modal__text">' + escapeHtml(content.x).replace(/\n/g, '<br>') + '</p>' +
      (metaParts.length ? '<div class="tp-review-modal__meta">' + metaParts.join('<span class="customer-reviews__meta-sep">·</span>') + '</div>' : '');
    lastFocus = document.activeElement;
    reviewModalEl.classList.add('tp-ai-modal--open');
    document.documentElement.classList.add('tp-ai-modal-open');
    var closeBtn = reviewModalEl.querySelector('.tp-ai-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeReviewModal() {
    if (!reviewModalEl) return;
    reviewModalEl.classList.remove('tp-ai-modal--open');
    document.documentElement.classList.remove('tp-ai-modal-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeModal(); closeReviewModal(); }
  });

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function renderContainer(inner, data, lang) {
    var track = inner.querySelector('.customer-reviews__track');
    if (!track || track.getAttribute('data-tp-rendered')) return;
    track.setAttribute('data-tp-rendered', '1');

    var i18n;
    try { i18n = JSON.parse(inner.getAttribute('data-i18n') || '{}'); } catch (e) { i18n = {}; }
    i18n.verified = i18n.verified || 'Verificada';
    i18n.translated = i18n.translated || 'Traducido';
    i18n.aiClose = i18n.aiClose || 'Cerrar';
    i18n.aiCta = i18n.aiCta || 'Trustpilot';
    i18n.allReviews = i18n.allReviews || 'Ver las {count} opiniones';
    i18n.allReviewsTitle = i18n.allReviewsTitle || 'Todas las opiniones';
    var opts = {
      i18n: i18n,
      url: inner.getAttribute('data-url') || '#',
      pack: getPack(lang),
      showAuthor: inner.getAttribute('data-show-author') !== 'false',
      showDate: inner.getAttribute('data-show-date') === 'true',
      obfuscateAuthor: inner.getAttribute('data-obfuscate-author') === 'true',
      // Separate desktop / mobile limits (theme settings); picked at render
      // time by viewport, matching the theme's 768px mobile breakpoint.
      truncateChars: parseInt(inner.getAttribute(
        window.matchMedia('(max-width: 767px)').matches ? 'data-truncate-chars-mobile' : 'data-truncate-chars'
      ), 10) || 0,
      rating: inner.getAttribute('data-rating') || '',
      totalReviews: inner.getAttribute('data-total-reviews') || ''
    };
    // Newest first in the dataset, so the carousel is simply the head of it.
    var maxReviews = parseInt(inner.getAttribute('data-max-reviews'), 10);
    if (!(maxReviews > 0)) maxReviews = DEFAULT_MAX_REVIEWS;
    var reviews = data.reviews.slice(0, maxReviews);

    var initial = '';
    var i;
    for (i = 0; i < Math.min(INITIAL_CARDS, reviews.length); i++) {
      initial += cardHtml(reviews[i], lang, opts, i);
    }
    track.innerHTML = initial;

    var appended = Math.min(INITIAL_CARDS, reviews.length);
    function appendChunk() {
      if (appended >= reviews.length) return;
      var html = '';
      var end = Math.min(appended + CHUNK, reviews.length);
      for (var j = appended; j < end; j++) html += cardHtml(reviews[j], lang, opts, j);
      appended = end;
      var frag = document.createElement('template');
      frag.innerHTML = html;
      track.appendChild(frag.content);
    }
    // Keep LOOKAHEAD cards rendered beyond the one the carousel is heading to.
    function ensureRendered(index) {
      while (appended < reviews.length && index + LOOKAHEAD >= appended) appendChunk();
    }
    initCarousel(inner, track, ensureRendered);

    // "Read more" on truncated cards opens the full-review modal instead of
    // following the card's Trustpilot link (delegated: cards render in chunks).
    inner.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tp-read-more]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var r = reviews[parseInt(btn.getAttribute('data-tp-read-more'), 10)];
      if (r) openReviewModal(r, lang, opts);
    });

    // AI summary button (lives outside the carousel inner, scoped per section)
    var section = inner.closest('[data-tp-section]') || inner.parentElement;
    var aiBtn = section ? section.querySelector('[data-tp-ai-open]') : null;
    if (aiBtn && !aiBtn.getAttribute('data-tp-bound')) {
      aiBtn.setAttribute('data-tp-bound', '1');
      aiBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(data, lang, i18n, opts.url, opts.pack, opts);
      });
    }
  }

  function boot() {
    var data = window.NextStepTPData;
    if (!data || !data.reviews) return;
    var lang = getLang();
    var containers = document.querySelectorAll('[data-tp-reviews]');
    for (var i = 0; i < containers.length; i++) renderContainer(containers[i], data, lang);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
